import {
	CodeBlock,
	Endpoint,
	Alert,
	ParamList,
	Param,
} from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function ApiOperators() {
	return (
		<div className={styles.docsPage}>
			<h1>Operator API Reference</h1>
			<p className={styles.lead}>
				The Operator API allows you to manage player sessions, handle deposits
				and withdrawals, and retrieve transaction and analytics data. All
				endpoints require authentication using your secret API key.
			</p>

			<Alert variant="info" title="Authentication Required">
				All Operator API endpoints require authentication using your secret API
				key in the Authorization header:{' '}
				<code>Authorization: Bearer sk_live_xxxxxxxxxxxxx</code>
			</Alert>

			{/* Sessions Endpoints */}
			<h2>Sessions</h2>
			<p>
				Sessions are the core mechanism for connecting your players to Ringfort
				games. Each session represents a unique play instance with its own
				balance tracking and provably fair seed pair.
			</p>

			{/* Create Session */}
			<h3 id="create-session">Create Player Session</h3>
			<Endpoint
				method="POST"
				path="/api/v1/operators/sessions"
				description="Creates a new game session for a player and returns a game URL with authentication token."
			/>

			<h4>Request Body</h4>
			<ParamList>
				<Param name="playerId" type="string" required>
					Unique identifier for the player in your system. This ID will be used
					to track all bets and transactions for this player.
				</Param>
				<Param name="currency" type="string" required>
					Three-letter currency code (e.g., <code>USD</code>, <code>EUR</code>,{' '}
					<code>BTC</code>, <code>ETH</code>). Determines the denomination for
					all bets and payouts.
				</Param>
				<Param name="walletMode" type="string" required>
					Wallet integration mode. Use <code>seamless</code> for real-time
					wallet callbacks or <code>integrated</code> for platform-managed
					balances.
				</Param>
				<Param name="returnUrl" type="string">
					URL to redirect players when they exit the game. If not provided,
					players will see a generic exit screen.
				</Param>
				<Param name="demo" type="boolean">
					Set to <code>true</code> to create a demo session with 10,000 virtual
					credits. Demo bets are not included in reports.
				</Param>
				<Param name="metadata" type="object">
					Custom key-value pairs to attach to the session. Useful for tracking
					campaigns, affiliates, or other custom data.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X POST https://api.rngfort.com/api/v1/operators/sessions \\
  -H "Authorization: Bearer sk_live_xxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "playerId": "player_abc123",
    "currency": "USD",
    "walletMode": "seamless",
    "returnUrl": "https://your-casino.com/lobby",
    "demo": false,
    "metadata": {
      "campaign": "summer_promo",
      "affiliate": "aff_001"
    }
  }'`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "sessionId": "sess_abc123xyz789",
    "sessionToken": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
    "gameUrl": "https://play.rngfort.com/play?token=eyJhbGci...",
    "expiresAt": 1704153600,
    "player": {
      "id": "player_abc123",
      "currency": "USD"
    }
  }
}`}
			</CodeBlock>

			{/* Get Session */}
			<h3 id="get-session">Get Session Info</h3>
			<Endpoint
				method="GET"
				path="/api/v1/operators/sessions/:id"
				description="Retrieves information about an existing session including its status, player details, and current balance."
			/>

			<h4>Path Parameters</h4>
			<ParamList>
				<Param name="id" type="string" required>
					The unique session identifier returned when the session was created.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X GET https://api.rngfort.com/api/v1/operators/sessions/sess_abc123xyz789 \\
  -H "Authorization: Bearer sk_live_xxxxxxxxxxxxx"`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "sessionId": "sess_abc123xyz789",
    "playerId": "player_abc123",
    "currency": "USD",
    "walletMode": "seamless",
    "status": "active",
    "balance": "50000",
    "totalBets": 125,
    "totalWagered": "250000",
    "totalWon": "245000",
    "createdAt": "2024-01-15T10:30:00Z",
    "expiresAt": "2024-01-15T22:30:00Z",
    "lastActivityAt": "2024-01-15T14:22:15Z",
    "metadata": {
      "campaign": "summer_promo",
      "affiliate": "aff_001"
    }
  }
}`}
			</CodeBlock>

			{/* Invalidate Session */}
			<h3 id="invalidate-session">Invalidate Session</h3>
			<Endpoint
				method="DELETE"
				path="/api/v1/operators/sessions/:id"
				description="Immediately invalidates a session, preventing any further game actions. Use this when a player logs out or you need to force-end a session."
			/>

			<h4>Path Parameters</h4>
			<ParamList>
				<Param name="id" type="string" required>
					The unique session identifier to invalidate.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X DELETE https://api.rngfort.com/api/v1/operators/sessions/sess_abc123xyz789 \\
  -H "Authorization: Bearer sk_live_xxxxxxxxxxxxx"`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "sessionId": "sess_abc123xyz789",
    "status": "invalidated",
    "invalidatedAt": "2024-01-15T14:30:00Z",
    "finalBalance": "52500"
  }
}`}
			</CodeBlock>

			<Alert variant="warning" title="Session Invalidation">
				Once a session is invalidated, it cannot be reactivated. Any pending
				game rounds will be settled before the session is fully terminated.
			</Alert>

			{/* Balance Endpoints */}
			<h2>Balance Management</h2>
			<p>
				Balance endpoints allow you to query and manage player balances when
				using integrated wallet mode. For seamless wallet mode, balances are
				managed through your wallet callbacks.
			</p>

			{/* Get Balance */}
			<h3 id="get-balance">Get Player Balance</h3>
			<Endpoint
				method="GET"
				path="/api/v1/operators/balance/:playerId"
				description="Retrieves the current balance for a player in integrated wallet mode."
			/>

			<h4>Path Parameters</h4>
			<ParamList>
				<Param name="playerId" type="string" required>
					The unique player identifier in your system.
				</Param>
			</ParamList>

			<h4>Query Parameters</h4>
			<ParamList>
				<Param name="currency" type="string">
					Filter balance by currency code. If not provided, returns all currency
					balances.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X GET "https://api.rngfort.com/api/v1/operators/balance/player_abc123?currency=USD" \\
  -H "Authorization: Bearer sk_live_xxxxxxxxxxxxx"`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "playerId": "player_abc123",
    "balances": [
      {
        "currency": "USD",
        "available": "50000",
        "locked": "0",
        "total": "50000"
      }
    ],
    "updatedAt": "2024-01-15T14:22:15Z"
  }
}`}
			</CodeBlock>

			{/* Deposit */}
			<h3 id="deposit">Deposit to Player</h3>
			<Endpoint
				method="POST"
				path="/api/v1/operators/deposit"
				description="Adds funds to a player's balance. Use this endpoint to credit player accounts when they make deposits on your platform."
			/>

			<h4>Request Body</h4>
			<ParamList>
				<Param name="playerId" type="string" required>
					The unique player identifier to credit.
				</Param>
				<Param name="amount" type="string" required>
					Amount to deposit in the smallest currency unit (e.g., cents for USD,
					satoshis for BTC). Must be a positive integer string.
				</Param>
				<Param name="currency" type="string" required>
					Three-letter currency code for the deposit.
				</Param>
				<Param name="transactionId" type="string" required>
					Your unique identifier for this deposit transaction. Used for
					idempotency and reconciliation.
				</Param>
				<Param name="description" type="string">
					Optional description for the transaction that will appear in reports.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X POST https://api.rngfort.com/api/v1/operators/deposit \\
  -H "Authorization: Bearer sk_live_xxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "playerId": "player_abc123",
    "amount": "10000",
    "currency": "USD",
    "transactionId": "dep_your_unique_id_001",
    "description": "Player deposit via credit card"
  }'`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "transactionId": "tx_rf_dep_abc123",
    "externalTransactionId": "dep_your_unique_id_001",
    "playerId": "player_abc123",
    "type": "deposit",
    "amount": "10000",
    "currency": "USD",
    "balanceBefore": "50000",
    "balanceAfter": "60000",
    "createdAt": "2024-01-15T14:35:00Z"
  }
}`}
			</CodeBlock>

			<Alert variant="info" title="Idempotency">
				Deposit requests are idempotent based on your <code>transactionId</code>
				. Sending the same request twice will return the original transaction
				without creating a duplicate deposit.
			</Alert>

			{/* Withdraw */}
			<h3 id="withdraw">Withdraw from Player</h3>
			<Endpoint
				method="POST"
				path="/api/v1/operators/withdraw"
				description="Removes funds from a player's balance. Use this endpoint when processing player withdrawal requests."
			/>

			<h4>Request Body</h4>
			<ParamList>
				<Param name="playerId" type="string" required>
					The unique player identifier to debit.
				</Param>
				<Param name="amount" type="string" required>
					Amount to withdraw in the smallest currency unit. Must be a positive
					integer string and cannot exceed the player's available balance.
				</Param>
				<Param name="currency" type="string" required>
					Three-letter currency code for the withdrawal.
				</Param>
				<Param name="transactionId" type="string" required>
					Your unique identifier for this withdrawal transaction.
				</Param>
				<Param name="description" type="string">
					Optional description for the transaction.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X POST https://api.rngfort.com/api/v1/operators/withdraw \\
  -H "Authorization: Bearer sk_live_xxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "playerId": "player_abc123",
    "amount": "5000",
    "currency": "USD",
    "transactionId": "wd_your_unique_id_002",
    "description": "Player withdrawal to bank account"
  }'`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "transactionId": "tx_rf_wd_def456",
    "externalTransactionId": "wd_your_unique_id_002",
    "playerId": "player_abc123",
    "type": "withdrawal",
    "amount": "5000",
    "currency": "USD",
    "balanceBefore": "60000",
    "balanceAfter": "55000",
    "createdAt": "2024-01-15T14:40:00Z"
  }
}`}
			</CodeBlock>

			{/* Transactions */}
			<h2>Transactions</h2>
			<p>
				Query and filter transaction history for your players including bets,
				wins, deposits, and withdrawals.
			</p>

			{/* List Transactions */}
			<h3 id="list-transactions">List Transactions</h3>
			<Endpoint
				method="GET"
				path="/api/v1/operators/transactions"
				description="Retrieves a paginated list of transactions with optional filtering by player, type, date range, and more."
			/>

			<h4>Query Parameters</h4>
			<ParamList>
				<Param name="playerId" type="string">
					Filter transactions by player ID.
				</Param>
				<Param name="type" type="string">
					Filter by transaction type: <code>bet</code>, <code>win</code>,{' '}
					<code>deposit</code>, <code>withdrawal</code>, <code>refund</code>.
				</Param>
				<Param name="currency" type="string">
					Filter by currency code.
				</Param>
				<Param name="gameId" type="string">
					Filter by game: <code>dice</code>, <code>plinko</code>,{' '}
					<code>coinflip</code>, <code>crash</code>.
				</Param>
				<Param name="startDate" type="string">
					Filter transactions from this date (ISO 8601 format).
				</Param>
				<Param name="endDate" type="string">
					Filter transactions until this date (ISO 8601 format).
				</Param>
				<Param name="limit" type="number">
					Number of results per page (1-100, default: 50).
				</Param>
				<Param name="cursor" type="string">
					Pagination cursor from previous response.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X GET "https://api.rngfort.com/api/v1/operators/transactions?playerId=player_abc123&type=bet&limit=20" \\
  -H "Authorization: Bearer sk_live_xxxxxxxxxxxxx"`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "transactions": [
      {
        "transactionId": "tx_bet_001",
        "type": "bet",
        "playerId": "player_abc123",
        "sessionId": "sess_abc123xyz789",
        "gameId": "dice",
        "betId": "bet_xyz789",
        "amount": "1000",
        "currency": "USD",
        "balanceAfter": "49000",
        "createdAt": "2024-01-15T14:20:00Z"
      },
      {
        "transactionId": "tx_win_001",
        "type": "win",
        "playerId": "player_abc123",
        "sessionId": "sess_abc123xyz789",
        "gameId": "dice",
        "betId": "bet_xyz789",
        "amount": "1980",
        "currency": "USD",
        "balanceAfter": "50980",
        "createdAt": "2024-01-15T14:20:01Z"
      }
    ],
    "pagination": {
      "hasMore": true,
      "cursor": "eyJsYXN0SWQiOiJ0eF93aW5fMDAxIn0=",
      "total": 125
    }
  }
}`}
			</CodeBlock>

			{/* Stats */}
			<h2>Analytics</h2>
			<p>
				Retrieve aggregated statistics and analytics for your operator account.
			</p>

			{/* Get Stats */}
			<h3 id="get-stats">Get Operator Stats</h3>
			<Endpoint
				method="GET"
				path="/api/v1/operators/stats"
				description="Retrieves aggregated statistics for your operator account including total wagered, GGR, player counts, and game breakdowns."
			/>

			<h4>Query Parameters</h4>
			<ParamList>
				<Param name="period" type="string">
					Time period for stats: <code>today</code>, <code>yesterday</code>,{' '}
					<code>week</code>, <code>month</code>, <code>all</code>. Default:{' '}
					<code>today</code>.
				</Param>
				<Param name="startDate" type="string">
					Custom start date (ISO 8601). Overrides <code>period</code> if
					provided.
				</Param>
				<Param name="endDate" type="string">
					Custom end date (ISO 8601). Overrides <code>period</code> if provided.
				</Param>
				<Param name="currency" type="string">
					Filter stats by currency. If not provided, returns stats in all
					currencies.
				</Param>
				<Param name="groupBy" type="string">
					Group results by: <code>day</code>, <code>week</code>,{' '}
					<code>month</code>, <code>game</code>. Default: no grouping.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X GET "https://api.rngfort.com/api/v1/operators/stats?period=week&groupBy=day" \\
  -H "Authorization: Bearer sk_live_xxxxxxxxxxxxx"`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "summary": {
      "totalBets": 15234,
      "totalWagered": "15234000",
      "totalWon": "14472300",
      "ggr": "761700",
      "ggrPercentage": 5.0,
      "uniquePlayers": 342,
      "activeSessions": 28,
      "currency": "USD"
    },
    "byGame": {
      "dice": {
        "bets": 8500,
        "wagered": "8500000",
        "won": "8075000",
        "ggr": "425000"
      },
      "plinko": {
        "bets": 4200,
        "wagered": "4200000",
        "won": "3990000",
        "ggr": "210000"
      },
      "coinflip": {
        "bets": 2534,
        "wagered": "2534000",
        "won": "2407300",
        "ggr": "126700"
      }
    },
    "timeSeries": [
      {
        "date": "2024-01-15",
        "bets": 2100,
        "wagered": "2100000",
        "won": "1995000",
        "ggr": "105000",
        "uniquePlayers": 89
      },
      {
        "date": "2024-01-14",
        "bets": 2350,
        "wagered": "2350000",
        "won": "2232500",
        "ggr": "117500",
        "uniquePlayers": 102
      }
    ]
  }
}`}
			</CodeBlock>

			{/* Rate Limits */}
			<h2>Rate Limits</h2>
			<p>
				The Operator API has the following rate limits to ensure fair usage and
				platform stability:
			</p>

			<CodeBlock language="json" title="Rate Limits">
				{`{
  "operator_api": {
    "requests_per_minute": 1000,
    "requests_per_hour": 50000,
    "burst_limit": 100
  }
}`}
			</CodeBlock>

			<Alert variant="info" title="Rate Limit Headers">
				All responses include rate limit headers: <code>X-RateLimit-Limit</code>
				, <code>X-RateLimit-Remaining</code>, and <code>X-RateLimit-Reset</code>
				.
			</Alert>

			{/* Next Steps */}
			<div className={styles.nextSteps}>
				<h2>Next Steps</h2>
				<ul>
					<li>
						<a href="/docs/api-games">Game API Reference</a> - Learn how to
						interact with games
					</li>
					<li>
						<a href="/docs/webhooks">Webhooks</a> - Set up real-time event
						notifications
					</li>
					<li>
						<a href="/docs/wallet-modes">Wallet Modes</a> - Understand seamless
						vs integrated wallets
					</li>
					<li>
						<a href="/docs/errors">Error Codes</a> - Handle errors gracefully
					</li>
				</ul>
			</div>
		</div>
	);
}
